import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  useDayOneDB,
  type DayOneDBTable,
} from "@/hooks/useDayOneDB";
import { useProjectEnvVars } from "@/hooks/useProjectEnvVars";
import { supabase } from "@/integrations/supabase/client";
import {
  Cloud,
  Database,
  Users,
  BarChart3,
  Key,
  FileText,
  Loader2,
  AlertTriangle,
  ChevronRight,
  Sparkles,
  ShieldCheck,
  ShieldAlert,
  Table2,
  RefreshCw,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  CheckCircle2,
  Clock,
  TrendingUp,
  Activity,
  Globe,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { CustomDomainPanel } from "./CustomDomainPanel";

interface DayOneCloudPanelProps {
  projectId: string;
  deployUrl?: string | null;
  onSendMessage?: (message: string) => void;
}

type CloudSection = "overview" | "database" | "users" | "analytics" | "secrets" | "logs" | "domain";

/* ------------------------------------------------------------------ */
/*  Overview Section                                                    */
/* ------------------------------------------------------------------ */

function OverviewSection({
  provisionedProject,
  tables,
  loading,
  provisioning,
  onProvision,
  deployUrl,
}: {
  provisionedProject: any;
  tables: DayOneDBTable[];
  loading: boolean;
  provisioning: boolean;
  onProvision: () => void;
  deployUrl?: string | null;
}) {
  const isActive = provisionedProject?.status === "active";
  const totalRows = tables.reduce((sum, t) => sum + t.row_count, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!provisionedProject || provisionedProject.status === "failed") {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-indigo-500/5 border border-indigo-500/20 flex items-center justify-center mb-5">
          <Cloud className="h-6 w-6 text-indigo-400" />
        </div>
        <h3 className="text-sm font-medium text-zinc-200 mb-2">No Cloud Database</h3>
        <p className="text-xs text-zinc-500 mb-5 max-w-[240px]">
          {provisionedProject?.status === "failed"
            ? provisionedProject.error_message || "Provisioning failed. Try again."
            : "Provision a database to enable auth, storage, and real-time data."}
        </p>
        <Button
          onClick={onProvision}
          disabled={provisioning}
          className="bg-indigo-600 hover:bg-indigo-500 text-white h-9 px-4 text-sm"
        >
          {provisioning ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Provisioning...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Provision Database
            </>
          )}
        </Button>
      </div>
    );
  }

  if (provisionedProject.status === "provisioning") {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="relative mx-auto w-12 h-12 mb-5">
          <div className="absolute inset-0 rounded-full bg-indigo-500/20 animate-ping" />
          <div className="relative w-12 h-12 rounded-full bg-indigo-500/10 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-400" />
          </div>
        </div>
        <h3 className="text-sm font-medium text-zinc-200 mb-2">Setting up your database</h3>
        <p className="text-xs text-zinc-500 mb-4">This usually takes 30-60 seconds</p>
        <div className="h-1 w-40 bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full bg-indigo-500/60 rounded-full animate-pulse" style={{ width: "60%" }} />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Status Card */}
      <div className="rounded-lg border border-zinc-800/50 bg-zinc-900/30 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-sm font-medium text-zinc-200">Active</span>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-zinc-500">Project URL</span>
            <span className="text-zinc-300 font-mono truncate max-w-[200px]">
              {provisionedProject.supabase_url || "—"}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-zinc-500">Region</span>
            <span className="text-zinc-300">{provisionedProject.region || "us-east-1"}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-zinc-500">Created</span>
            <span className="text-zinc-300">
              {provisionedProject.created_at
                ? formatDistanceToNow(new Date(provisionedProject.created_at), { addSuffix: true })
                : "—"}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-zinc-800/50 bg-zinc-900/30 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Table2 className="h-4 w-4 text-indigo-400" />
            <span className="text-xs text-zinc-500">Tables</span>
          </div>
          <p className="text-2xl font-semibold text-zinc-200">{tables.length}</p>
        </div>
        <div className="rounded-lg border border-zinc-800/50 bg-zinc-900/30 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Database className="h-4 w-4 text-indigo-400" />
            <span className="text-xs text-zinc-500">Total Rows</span>
          </div>
          <p className="text-2xl font-semibold text-zinc-200">{totalRows.toLocaleString()}</p>
        </div>
      </div>

      {/* Deploy URL */}
      {deployUrl && (
        <div className="rounded-lg border border-zinc-800/50 bg-zinc-900/30 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="h-4 w-4 text-emerald-400" />
            <span className="text-xs text-zinc-500">Deployed App</span>
          </div>
          <a
            href={deployUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-indigo-400 hover:underline font-mono truncate block"
          >
            {deployUrl}
          </a>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Database Section (existing DayOneDBPanel content)                   */
/* ------------------------------------------------------------------ */

function DatabaseSection({
  tables,
  tablesLoading,
  onFetchTables,
  onFetchTableData,
  provisionedProject,
  onSendMessage,
}: {
  tables: DayOneDBTable[];
  tablesLoading: boolean;
  onFetchTables: () => void;
  onFetchTableData: (name: string, limit: number) => Promise<any>;
  provisionedProject: any;
  onSendMessage?: (message: string) => void;
}) {
  const [openTable, setOpenTable] = useState<string | null>(null);
  const [dataViewTable, setDataViewTable] = useState<string | null>(null);
  const [loadingDataFor, setLoadingDataFor] = useState<string | null>(null);
  const [tableDataCache, setTableDataCache] = useState<Record<string, Record<string, unknown>[]>>({});

  const handleFetchData = useCallback(async (tableName: string) => {
    if (tableDataCache[tableName]) return;
    setLoadingDataFor(tableName);
    const data = await onFetchTableData(tableName, 20);
    if (data) {
      setTableDataCache((prev) => ({ ...prev, [tableName]: data }));
    }
    setLoadingDataFor(null);
  }, [onFetchTableData, tableDataCache]);

  if (!provisionedProject || provisionedProject.status !== "active") {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <Database className="h-10 w-10 text-zinc-700 mb-4" />
        <p className="text-sm text-zinc-500">Provision a database first</p>
      </div>
    );
  }

  if (tablesLoading && tables.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (tables.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <Table2 className="h-10 w-10 text-zinc-700 mb-4" />
        <p className="text-sm text-zinc-400 mb-2">No tables yet</p>
        <p className="text-xs text-zinc-600 mb-5">Ask the AI to add database support</p>
        <Button
          onClick={() => onSendMessage?.("Add a database with the right tables for this app")}
          variant="outline"
          size="sm"
          className="bg-transparent border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
        >
          <Sparkles className="h-3.5 w-3.5 mr-1.5" />
          Add database
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/50">
        <span className="text-sm font-medium text-zinc-300">{tables.length} Tables</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50"
          onClick={onFetchTables}
          disabled={tablesLoading}
        >
          <RefreshCw className={`h-3 w-3 mr-1.5 ${tablesLoading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-0.5">
          {tables.map((table) => (
            <Collapsible
              key={table.name}
              open={openTable === table.name}
              onOpenChange={() => setOpenTable(openTable === table.name ? null : table.name)}
            >
              <CollapsibleTrigger asChild>
                <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.03] transition-all text-left">
                  <ChevronRight
                    className={`h-3.5 w-3.5 text-zinc-500 transition-transform ${openTable === table.name ? "rotate-90" : ""}`}
                  />
                  <Table2 className="h-4 w-4 text-indigo-400/70" />
                  <span className="text-sm font-medium text-zinc-200 truncate flex-1">{table.name}</span>
                  {table.rls_enabled ? (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/10 text-emerald-400">
                      <ShieldCheck className="h-3 w-3" />
                      RLS
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-red-500/10 text-red-400">
                      <ShieldAlert className="h-3 w-3" />
                    </span>
                  )}
                  <span className="text-[11px] text-zinc-500 font-mono">{table.row_count}</span>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-10 pr-3 pb-3 pt-1">
                <div className="flex items-center gap-2 mb-3">
                  <button
                    onClick={() => setDataViewTable(dataViewTable === table.name ? null : null)}
                    className={`text-[11px] px-2 py-1 rounded ${dataViewTable !== table.name ? "bg-zinc-700 text-zinc-200" : "text-zinc-500 hover:text-zinc-300"}`}
                  >
                    Schema
                  </button>
                  <button
                    onClick={() => {
                      handleFetchData(table.name);
                      setDataViewTable(table.name);
                    }}
                    className={`text-[11px] px-2 py-1 rounded ${dataViewTable === table.name ? "bg-zinc-700 text-zinc-200" : "text-zinc-500 hover:text-zinc-300"}`}
                  >
                    Data
                  </button>
                </div>
                {dataViewTable !== table.name ? (
                  <div className="space-y-1.5 border-l border-zinc-800 pl-3">
                    {table.columns.map((col) => (
                      <div key={col.name} className="flex items-center gap-2 text-xs">
                        <span className="text-zinc-300 font-medium">{col.name}</span>
                        <span className="text-zinc-600 font-mono text-[11px]">{col.type}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border border-zinc-800 rounded-lg overflow-hidden">
                    {loadingDataFor === table.name ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
                      </div>
                    ) : tableDataCache[table.name]?.length ? (
                      <div className="overflow-x-auto max-h-[200px] overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-zinc-800/50 sticky top-0">
                            <tr>
                              {Object.keys(tableDataCache[table.name][0]).map((key) => (
                                <th key={key} className="px-2 py-1.5 text-left font-medium text-zinc-400 whitespace-nowrap">
                                  {key}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {tableDataCache[table.name].map((row, i) => (
                              <tr key={i} className="border-t border-zinc-800/50">
                                {Object.values(row).map((val, j) => (
                                  <td key={j} className="px-2 py-1.5 text-zinc-300 whitespace-nowrap max-w-[150px] truncate">
                                    {val === null ? <span className="text-zinc-600 italic">null</span> : String(val)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="py-6 text-center text-zinc-500 text-xs">No data</div>
                    )}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Users Section                                                       */
/* ------------------------------------------------------------------ */

function UsersSection({ projectId, provisionedProject }: { projectId: string; provisionedProject: any }) {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [userCount, setUserCount] = useState(0);

  useEffect(() => {
    if (!projectId || provisionedProject?.status !== "active") return;

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("apply-schema", {
          body: {
            projectId,
            action: "query",
            sql: `SELECT id, email, created_at, last_sign_in_at FROM auth.users ORDER BY created_at DESC LIMIT 50`,
          },
        });
        if (!error && data?.rows) {
          setUsers(data.rows);
          setUserCount(data.rows.length);
        }
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [projectId, provisionedProject?.status]);

  if (!provisionedProject || provisionedProject.status !== "active") {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <Users className="h-10 w-10 text-zinc-700 mb-4" />
        <p className="text-sm text-zinc-500">Provision a database first</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-zinc-800/50">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-zinc-300">Authenticated Users</span>
          <span className="text-xs text-zinc-500">{userCount} total</span>
        </div>
      </div>

      {/* Signups Chart Placeholder */}
      <div className="px-4 py-3 border-b border-zinc-800/50">
        <p className="text-xs text-zinc-500 mb-2">Last 7 days</p>
        <div className="h-16 flex items-end gap-1">
          {[3, 5, 2, 8, 4, 6, 7].map((v, i) => (
            <div
              key={i}
              className="flex-1 bg-indigo-500/30 rounded-t"
              style={{ height: `${(v / 8) * 100}%` }}
            />
          ))}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-zinc-600">7d ago</span>
          <span className="text-[10px] text-zinc-600">Today</span>
        </div>
      </div>

      <ScrollArea className="flex-1">
        {users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <Users className="h-10 w-10 text-zinc-700 mb-4" />
            <p className="text-sm text-zinc-400">No users yet</p>
            <p className="text-xs text-zinc-600 mt-1">Users will appear here when they sign up</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {users.map((user) => (
              <div
                key={user.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.03]"
              >
                <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
                  <span className="text-xs font-medium text-indigo-400">
                    {(user.email || "U").charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-200 truncate">{user.email || "—"}</p>
                  <p className="text-[11px] text-zinc-500">
                    Joined {user.created_at ? formatDistanceToNow(new Date(user.created_at), { addSuffix: true }) : "—"}
                  </p>
                </div>
                {user.last_sign_in_at && (
                  <span className="text-[10px] text-zinc-600">
                    Last seen {formatDistanceToNow(new Date(user.last_sign_in_at), { addSuffix: true })}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Analytics Section                                                   */
/* ------------------------------------------------------------------ */

function AnalyticsSection({ projectId, deployUrl }: { projectId: string; deployUrl?: string | null }) {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<any>(null);

  useEffect(() => {
    if (!projectId || !deployUrl) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const { data } = await supabase.functions.invoke("get-analytics", {
          body: { project_id: projectId },
        });
        if (data && !data.error) setAnalytics(data);
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [projectId, deployUrl]);

  if (!deployUrl) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <BarChart3 className="h-10 w-10 text-zinc-700 mb-4" />
        <p className="text-sm text-zinc-400">Deploy your app first</p>
        <p className="text-xs text-zinc-600 mt-1">Analytics appear after deployment</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  const hasData = analytics && (analytics.pageviews30d > 0 || analytics.uniqueVisitors30d > 0);

  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <TrendingUp className="h-10 w-10 text-zinc-700 mb-4" />
        <p className="text-sm text-zinc-400">No traffic yet</p>
        <p className="text-xs text-zinc-600 mt-1">Share your app to start tracking</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-zinc-800/50 bg-zinc-900/30 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="h-4 w-4 text-indigo-400" />
            <span className="text-xs text-zinc-500">Page Views (30d)</span>
          </div>
          <p className="text-2xl font-semibold text-zinc-200">{analytics.pageviews30d?.toLocaleString() || 0}</p>
        </div>
        <div className="rounded-lg border border-zinc-800/50 bg-zinc-900/30 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-indigo-400" />
            <span className="text-xs text-zinc-500">Visitors (30d)</span>
          </div>
          <p className="text-2xl font-semibold text-zinc-200">{analytics.uniqueVisitors30d?.toLocaleString() || 0}</p>
        </div>
      </div>

      {analytics.topPages?.length > 0 && (
        <div className="rounded-lg border border-zinc-800/50 bg-zinc-900/30 p-3">
          <p className="text-xs text-zinc-500 mb-3">Top Pages</p>
          <div className="space-y-2">
            {analytics.topPages.slice(0, 5).map((page: any, i: number) => (
              <div key={page.path} className="flex items-center justify-between text-xs">
                <span className="text-zinc-400">{i + 1}.</span>
                <span className="flex-1 mx-2 font-mono text-zinc-300 truncate">{page.path}</span>
                <span className="text-zinc-500">{page.views}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] text-zinc-600 text-center">Powered by PostHog + Vercel Analytics</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Secrets Section                                                     */
/* ------------------------------------------------------------------ */

function SecretsSection({ projectId }: { projectId: string }) {
  const { envVars, loading, saving, fetchEnvVars, setEnvVar, deleteEnvVar } = useProjectEnvVars(projectId);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [showValue, setShowValue] = useState(false);

  useEffect(() => {
    fetchEnvVars();
  }, [fetchEnvVars]);

  const handleAdd = async () => {
    const key = newKey.trim().toUpperCase();
    if (!key || !newValue.trim()) return;
    const success = await setEnvVar(key, newValue);
    if (success) {
      setNewKey("");
      setNewValue("");
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-zinc-800/50 space-y-3">
        <p className="text-xs text-zinc-500">Add environment variables for your deployed app</p>
        <div className="flex gap-2">
          <Input
            placeholder="KEY_NAME"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
            className="flex-1 h-9 bg-zinc-900/50 border-zinc-800 text-zinc-200 font-mono text-sm"
          />
          <div className="flex-1 relative">
            <Input
              type={showValue ? "text" : "password"}
              placeholder="Value"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              className="h-9 bg-zinc-900/50 border-zinc-800 text-zinc-200 font-mono text-sm pr-10"
            />
            <button
              onClick={() => setShowValue(!showValue)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
            >
              {showValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <Button
            onClick={handleAdd}
            disabled={!newKey.trim() || !newValue.trim() || saving}
            size="icon"
            className="h-9 w-9 bg-indigo-600 hover:bg-indigo-500"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
          </div>
        ) : envVars.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <Key className="h-10 w-10 text-zinc-700 mb-4" />
            <p className="text-sm text-zinc-400">No secrets yet</p>
            <p className="text-xs text-zinc-600 mt-1">Add API keys and secrets above</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {envVars.map((envVar) => (
              <div
                key={envVar.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.03]"
              >
                <Key className="h-4 w-4 text-zinc-600" />
                <span className="text-sm font-mono text-zinc-200 flex-1">{envVar.key}</span>
                <span className="text-xs text-zinc-600">••••••••</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-zinc-600 hover:text-red-400"
                  onClick={() => deleteEnvVar(envVar.key)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Logs Section                                                        */
/* ------------------------------------------------------------------ */

function LogsSection({ projectId }: { projectId: string }) {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Placeholder - in production this would fetch from Supabase logs API
    setLoading(true);
    const mockLogs = [
      { id: "1", type: "deploy", status: "success", message: "Deployed to Vercel", ts: Date.now() - 3600000 },
      { id: "2", type: "schema", status: "success", message: "Applied migration 001_initial", ts: Date.now() - 7200000 },
      { id: "3", type: "deploy", status: "success", message: "Deployed to Vercel", ts: Date.now() - 86400000 },
    ];
    setTimeout(() => {
      setLogs(mockLogs);
      setLoading(false);
    }, 500);
  }, [projectId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-zinc-800/50">
        <span className="text-sm font-medium text-zinc-300">Recent Activity</span>
      </div>
      <ScrollArea className="flex-1">
        {logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <FileText className="h-10 w-10 text-zinc-700 mb-4" />
            <p className="text-sm text-zinc-400">No logs yet</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {logs.map((log) => (
              <div
                key={log.id}
                className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.03]"
              >
                {log.status === "success" ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-200">{log.message}</p>
                  <p className="text-[11px] text-zinc-500">
                    {formatDistanceToNow(new Date(log.ts), { addSuffix: true })}
                  </p>
                </div>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded ${
                    log.type === "deploy"
                      ? "bg-indigo-500/10 text-indigo-400"
                      : "bg-emerald-500/10 text-emerald-400"
                  }`}
                >
                  {log.type}
                </span>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Panel                                                          */
/* ------------------------------------------------------------------ */

export function DayOneCloudPanel({ projectId, deployUrl, onSendMessage }: DayOneCloudPanelProps) {
  const [activeSection, setActiveSection] = useState<CloudSection>("overview");
  const {
    provisionedProject,
    tables,
    loading,
    tablesLoading,
    provisioning,
    provision,
    fetchTables,
    fetchTableData,
  } = useDayOneDB(projectId);

  const navItems: { id: CloudSection; label: string; icon: React.ElementType }[] = [
    { id: "overview", label: "Overview", icon: Cloud },
    { id: "database", label: "Database", icon: Database },
    { id: "users", label: "Users", icon: Users },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "secrets", label: "Secrets", icon: Key },
    { id: "domain", label: "Domain", icon: Globe },
    { id: "logs", label: "Logs", icon: FileText },
  ];

  return (
    <div className="flex h-full" style={{ background: "#080810" }}>
      {/* Left Sidebar Nav */}
      <div className="w-[140px] border-r border-zinc-800/50 flex flex-col py-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`flex items-center gap-2.5 px-4 py-2.5 text-left transition-colors ${
                isActive
                  ? "bg-indigo-500/10 border-r-2 border-indigo-500 text-indigo-400"
                  : "text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.02]"
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="text-xs font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {activeSection === "overview" && (
          <OverviewSection
            provisionedProject={provisionedProject}
            tables={tables}
            loading={loading}
            provisioning={provisioning}
            onProvision={provision}
            deployUrl={deployUrl}
          />
        )}
        {activeSection === "database" && (
          <DatabaseSection
            tables={tables}
            tablesLoading={tablesLoading}
            onFetchTables={fetchTables}
            onFetchTableData={fetchTableData}
            provisionedProject={provisionedProject}
            onSendMessage={onSendMessage}
          />
        )}
        {activeSection === "users" && (
          <UsersSection projectId={projectId} provisionedProject={provisionedProject} />
        )}
        {activeSection === "analytics" && (
          <AnalyticsSection projectId={projectId} deployUrl={deployUrl} />
        )}
        {activeSection === "secrets" && <SecretsSection projectId={projectId} />}
        {activeSection === "domain" && (
          <div className="p-4">
            <CustomDomainPanel projectId={projectId} />
          </div>
        )}
        {activeSection === "logs" && <LogsSection projectId={projectId} />}
      </div>
    </div>
  );
}
